// src/auth/Login.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginAndGetBackendToken } from '../auth/authService';

import './Login.css';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/Finanzas';

  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem('remember_email'));

  // Recordar correo (preferencia del usuario)
  useEffect(() => {
    try {
      const remembered = localStorage.getItem('remember_email');
      if (remembered) setEmail(remembered);
    } catch {}
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setInfo('');

    if (!email || !pwd) {
      setErr('Completa todos los campos.');
      return;
    }

    try {
      setBusy(true);
      if (remember) localStorage.setItem('remember_email', email.trim());
      else localStorage.removeItem('remember_email');

      await loginAndGetBackendToken(email.trim(), pwd);
      navigate(from, { replace: true });
    } catch (e) {
      if (e?.status === 401) setErr('Credenciales inválidas. Verifica usuario y contraseña.');
      else if (e?.status === 403) setErr(e.message || 'Tu cuenta está deshabilitada. Contacta al administrador.');
      else if (e?.status === 429) setErr('Demasiados intentos fallidos. Intenta más tarde.');
      else if (e instanceof TypeError) setErr('Fallo de red. Revisa tu conexión.');
      else setErr(e?.message || 'Error en el login.');
    } finally {
      setBusy(false);
    }
  };

  // Sin correo automático: el administrador restablece la contraseña desde Usuarios.
  const onForgot = (e) => {
    e.preventDefault();
    setErr(''); setInfo('');
    setInfo('Pide a un administrador que restablezca tu contraseña desde la pantalla de Usuarios.');
  };

  return (
    <div className="login-shell">
      <div className="login-frame">
        <aside className="hero">
          <div className="hero-inner">
            <div className="hero-badge">
              <img src="img/Logosinfondo.png" alt="Brand" className="brand-logo"/>
            </div>
            <div className="hero-brand">American Pizza By Vipizza</div>
            <h1 className="hero-title">Sistema<br/>Finanzas</h1>
            <p className="hero-sub"> </p>
          </div>
          <div className="hero-pattern" aria-hidden />
        </aside>

        <main className="form-area">
          <div className="form-card shadow-sm">
            <h3 className="form-title">Iniciar sesión</h3>

            {err && <div className="alert alert-danger py-2 small mb-3">{err}</div>}
            {info && <div className="alert alert-success py-2 small mb-3">{info}</div>}

            <form onSubmit={onSubmit} noValidate>
              {/* Email (con datalist de sugerencias si existen) */}
              <div className="mb-3">
                <label className="form-label" htmlFor="emailInput">Usuario (correo)</label>
                <div className="input-with-icon">
                  <span className="icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#0F3D2E">
                      <path d="M12 12c2.76 0 5-2.69 5-6s-2.24-6-5-6-5 2.69-5 6 2.24 6 5 6Zm0 2c-4.42 0-8 3.13-8 7v1h16v-1c0-3.87-3.58-7-8-7Z"/>
                    </svg>
                  </span>
                  <input
                    id="emailInput"
                    type="email"
                    className="form-control"
                    placeholder="email@dominio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    disabled={busy}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-2">
                <label className="form-label" htmlFor="password">Contraseña</label>
                <div className="input-with-icon">
                  <span className="icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#0F3D2E">
                      <path d="M17 9V7a5 5 0 0 0-10 0v2H5v13h14V9h-2Zm-8 0V7a3 3 0 0 1 6 0v2H9Zm3 5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/>
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    className="form-control"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoComplete="current-password"
                    required
                    disabled={busy}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="toggle-pass"
                    onClick={() => setShowPwd(s => !s)}
                    aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={busy}
                  >
                    {showPwd ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#0F3D2E">
                        <path d="M2 2l20 20-1.5 1.5L18 19.5c-1.8 1-3.8 1.5-6 1.5-5.5 0-10-3.5-12-9 1-2.7 2.7-4.9 4.8-6.5L.5 3.5 2 2Zm5.7 5.7C9.3 7 10.6 6.5 12 6.5c4.1 0 7.5 2.4 9.2 6-1 2.3-2.6 4-4.6 5.1l-2.3-2.3A4.5 4.5 0 0 0 9 9l-1.3-1.3Z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#0F3D2E">
                        <path d="M12 5c-5 0-9.3 3-11 7 1.7 4 6 7 11 7s9.3-3 11-7c-1.7-4-6-7-11-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="remember-row">
                <div className="form-check m-0">
                  <input
                    id="rememberMe"
                    className="form-check-input"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    disabled={busy}
                  />
                  <label className="form-check-label" htmlFor="rememberMe">Recordar usuario</label>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={onForgot}
                  onKeyDown={(e) => e.key === 'Enter' && onForgot(e)}
                  className="forgot-link"
                >
                  ¿Olvidaste tu contraseña?
                </span>
              </div>

              <button className="btn btn-primary w-100" disabled={busy}>
                {busy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Ingresando…
                  </>
                ) : 'Iniciar sesión'}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
